/**
 * Snapchat Conversions API (CAPI) Adapter
 *
 * Adapts standardized event payloads into Snapchat CAPI format.
 * Handles both browser pixel and server-to-server (S2S) data structures.
 *
 * @see https://docs.snap.com/api/marketing-api/Conversions-API/Parameters
 * @see https://businesshelp.snapchat.com/s/article/pixel-direct-implementation
 */

(function (pmw) {
	"use strict";

	/**
	 * Snapchat Event Adapter
	 */
	class SnapchatAdapter {
		/**
		 * Adapt event data to Snapchat format
		 *
		 * @param {object} eventData - Standardized event data
		 * @param {string} eventName - Event name (e.g., "add_to_cart")
		 * @param {object} context - Additional context (user, page, etc.)
		 * @returns {object|null} Snapchat-formatted event data or null if event not supported
		 */
		adapt(eventData, eventName, context) {
			// Only process if Snapchat pixel is loaded
			if (!pmwDataLayer?.pixels?.snapchat?.loaded) {
				return null;
			}

			// Map event names to adaptation methods
			const eventMap = {
				add_to_cart    : () => this.adaptAddCart(eventData),
				view_item      : () => this.adaptViewContent(eventData),
				begin_checkout : () => this.adaptStartCheckout(eventData),
				purchase       : () => this.adaptPurchase(eventData),
				search         : () => this.adaptSearch(eventData),
				add_to_wishlist: () => this.adaptAddToWishlist(eventData),
				view_category  : () => this.adaptViewContent(eventData),
				page_view      : () => this.adaptPageView(eventData),
				place_order    : () => this.adaptPlaceOrder(eventData),
				login          : () => this.adaptLogin(eventData),
				signup         : () => this.adaptSignUp(eventData),
			};

			if (eventMap[eventName]) {
				return eventMap[eventName]();
			}

			return null;
		}

		/**
		 * Get base Snapchat event data structure
		 *
		 * @returns {object} Base event data
		 */
		getBaseData() {

			// The user data the Conversions API needs comes from
			// functions_premium.js, which the free build does not ship. The
			// browser pixel only reads event_id and the search string.
			if (typeof pmw.getSnapchatS2SBaseData === "function") {
				return pmw.getSnapchatS2SBaseData();
			}

			return {
				event_id        : pmw.getRandomEventId(),
				event_time      : Math.floor(Date.now() / 1000),
				event_source_url: window.location.href,
				action_source   : "WEB",
			};
		}

		/**
		 * Adapt AddCart event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Snapchat ADD_CART event
		 */
		adaptAddCart(eventData) {
			const data      = this.getBaseData();
			data.event_name = "ADD_CART";

			if (eventData.product) {
				data.custom_data = pmw.getSnapchatCustomDataForProduct(eventData.product);
			}

			return data;
		}

		/**
		 * Adapt ViewContent event (single product or category view)
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Snapchat VIEW_CONTENT event
		 */
		adaptViewContent(eventData) {
			const data      = this.getBaseData();
			data.event_name = "VIEW_CONTENT";

			if (eventData.product) {
				data.custom_data = pmw.getSnapchatCustomDataForProduct(eventData.product);
			}

			return data;
		}

		/**
		 * Adapt StartCheckout event
		 *
		 * @param {object} eventData - Event data with cart items
		 * @returns {object} Snapchat START_CHECKOUT event
		 */
		adaptStartCheckout(eventData) {
			const data      = this.getBaseData();
			data.event_name = "START_CHECKOUT";

			// begin_checkout dispatches with an empty eventData, so the cart is
			// read from the data layer, the same way the other adapters do it.
			// Reading eventData here left every event without a price.
			if (pmwDataLayer?.cart && !pmw.isEmptyObject(pmwDataLayer.cart)) {

				const cartItems = Object.values(pmwDataLayer.cart);

				data.custom_data = {
					price       : pmw.getCartValue(),
					currency    : pmwDataLayer?.shop?.currency,
					number_items: cartItems.reduce((total, item) => total + item.quantity, 0),
					item_ids    : cartItems.map(item => pmw.getProductIdForSpecificPixel(item, "snapchat")).filter(id => null !== id),
				};
			}

			return data;
		}

		/**
		 * Adapt Purchase event
		 *
		 * @param {object} eventData - Event data with transaction information
		 * @returns {object} Snapchat PURCHASE event
		 */
		adaptPurchase(eventData) {
			const data = this.getBaseData();
			// Use order ID for purchase event to match server-side event ID
			if (pmwDataLayer?.order?.id) {
				data.event_id = "pmw_" + pmwDataLayer.order.id.toString();
			}
			data.event_name = "PURCHASE";

			if (eventData.transaction) {
				const transaction = eventData.transaction;
				data.custom_data  = {
					price         : transaction.value || 0,
					currency      : transaction.currency || pmwDataLayer?.shop?.currency,
					transaction_id: transaction.transaction_id || transaction.order_id,
				};

				if (transaction.items && transaction.items.length > 0) {
					data.custom_data.number_items = transaction.items.reduce((total, item) => total + item.quantity, 0);
					data.custom_data.item_ids     = transaction.items.map(item => pmw.getProductIdForSpecificPixel(item, "snapchat")).filter(id => null !== id);
				}
			}

			return data;
		}

		/**
		 * Adapt Search event
		 *
		 * @param {object} eventData - Event data with search query
		 * @returns {object} Snapchat SEARCH event
		 */
		adaptSearch(eventData) {
			const data      = this.getBaseData();
			data.event_name = "SEARCH";

			data.custom_data = {
				search_string: pmw.getSearchTermFromUrl(),
			};

			return data;
		}

		/**
		 * Adapt AddToWishlist event
		 *
		 * @param {object} eventData - Event data with product information
		 * @returns {object} Snapchat ADD_TO_WISHLIST event
		 */
		adaptAddToWishlist(eventData) {
			const data      = this.getBaseData();
			data.event_name = "ADD_TO_WISHLIST";

			if (eventData.product) {
				data.custom_data = pmw.getSnapchatCustomDataForProduct(eventData.product);
			}

			return data;
		}

		/**
		 * Adapt PageView event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} Snapchat PAGE_VIEW event
		 */
		adaptPageView(eventData) {
			const data      = this.getBaseData();
			data.event_name = "PAGE_VIEW";

			return data;
		}

		/**
		 * Adapt SignUp event
		 *
		 * @param {object} eventData - Event data with user information
		 * @returns {object} Snapchat SIGN_UP event
		 */
		adaptSignUp(eventData) {
			const data      = this.getBaseData();
			data.event_name = "SIGN_UP";

			return data;
		}

		/**
		 * Adapt PlaceOrder event
		 *
		 * Note: Snapchat doesn't have a specific PlaceOrder event.
		 * Return null as this event is not supported.
		 *
		 * @param {object} eventData - Event data
		 * @returns {null} Snapchat doesn't support place order event
		 */
		adaptPlaceOrder(eventData) {
			// Snapchat CAPI doesn't have a PlaceOrder event
			return null;
		}

		/**
		 * Adapt Login event
		 *
		 * @param {object} eventData - Event data
		 * @returns {object} Snapchat LOGIN event
		 */
		adaptLogin(eventData) {
			const data      = this.getBaseData();
			data.event_name = "LOGIN";

			return data;
		}
	}

	// Register the adapter with S2S capabilities
	const adapter = new SnapchatAdapter();
	pmw.registerAdapter("snapchat", adapter, {
		supportsS2S               : true,
		supportsEventDeduplication: true,
		supportsPageViewS2S       : true,
		isActive                  : () => pmwDataLayer?.pixels?.snapchat?.loaded === true && pmw.consent.categories.get().marketing === true,
		isConfigured              : () => !!pmwDataLayer?.pixels?.snapchat?.pixel_id,
	});

})(window.pmw = window.pmw || {});
